import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { logActivity } from '../utils/activityLogger';
import '../styles/comment-form.css';

const CommentForm = ({ contentId, onCommentAdded, placeholder, isReply = false }) => {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const defaultPlaceholder = isReply ? t('news.comments.addReply') : t('news.comments.addComment');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim() || !user) return;

    logActivity('comment_submit', 'content', contentId, 
      isReply ? 'User submitted a reply' : 'User submitted a comment',
      { isReply, contentLength: content.trim().length, parentContentId: contentId });

    setSubmitting(true);
    
    try {
      const response = await fetch('/api/content?operation=createContent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentType: 'comment',
          contentText: content.trim(),
          status: 'published',
          visibility: 'public',
          parentContentId: contentId,
          authorUserId: user.id,
          authorName: user.name || user.email,
          authorEmail: user.email,
          userRole: user.role
        })
      });

      const newComment = await response.json();
      
      if (response.ok) {
        setContent('');
        onCommentAdded(newComment);
        
        // Update parent comment count
        if (isReply) {
          await fetch(`/api/content?operation=updateContent&contentId=${contentId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              commentCount: 'increment',
              userId: user.id,
              userRole: user.role
            })
          });
        }
      } else {
        console.error('Error creating comment:', newComment.error);
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="comment-form-login">
        {t('news.comments.loginToComment')}
      </div>
    );
  }

  return (
    <form className={`comment-form ${isReply ? 'reply-form' : ''}`} onSubmit={handleSubmit}>
      <div className="comment-input-container">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder || defaultPlaceholder}
          className="comment-input"
          rows={isReply ? 2 : 3}
          disabled={submitting}
        />
      </div>
      
      <div className="comment-form-actions">
        <button
          type="submit"
          disabled={!content.trim() || submitting}
          className="comment-submit"
        >
          {submitting ? t('news.comments.posting') : (isReply ? t('news.comments.reply') : t('news.comments.comment'))}
        </button>
      </div>
    </form>
  );
};

export default CommentForm;